import * as _ from 'lodash';
import { ElementType, IContentElement, IQuestionConfig } from "src/app/ui-testrunner/models";
import { ItemSetFrameworkCtrl } from "../../item-set-editor/controllers/framework";
import { ItemBankCtrl } from "../../item-set-editor/controllers/item-bank";
import { AuditCheckType, DndCheckFlags, EAuditElementType, IAuditCheckByBlockType, IAuditCheckByContainsBlockType, IAuditCheckByFunctions, IAuditCheckByPropConfig, IAuditCheckByPropVal, IAuditConfig, InformativeCheckType, SubAuditExcludedItems, TargetElementGrouping, TAuditCheck } from '../data/audits'
import { checkPropCondVal, applyPropTransform, getQuestionDeepElements, isNullOrEmptyOrUndefined, getQuestionRespondableDeep } from './content-analysis';
import { IContentElementOrder } from 'src/app/ui-testrunner/element-render-order/model';
import { IContentElementInsertion } from 'src/app/ui-testrunner/element-render-insertion/model';
import { IContentElementMoveableDragDrop } from 'src/app/ui-testrunner/element-render-moveable-dnd/model';
import { IContentElementGroup } from 'src/app/ui-testrunner/element-render-grouping/model';
import { IAssembledPanel, IAssessmentPartition, IPanelModulesConfig } from '../../item-set-editor/models/assessment-framework';
import { ITargetPropArrayFilterConfig, TCheckFunctionType } from '../data/checks';
import { IItemContentDiff, ILegacyItemContentDiff } from './content-diff';

interface IContext {
    itemBankCtrl: ItemBankCtrl, // todo: highly stateful
    frameworkCtrl: ItemSetFrameworkCtrl, // todo: highly stateful
    customCheckFunctions?: TCheckFunctionType,
    auditSlug?: string,
    checkId?: string
}

export interface IAuditResult { 
  id: string, 
  caption: string, 
  typeCaption?: string, 
  items?: any[], 
  list?:any[], // might be specific to 'VOICEOVER'
  type?: EAuditElementType,
  excludedItems?: SubAuditExcludedItems[], // specific item types we wan't to exclude from a subaudit but not the entire audit
  informativeOnly?: boolean,
  autoFixContext?: any,
  /** Unique param codes flagged by this check (column collector). */
  collectedParamCodes?: string[]
};

export interface AuditResultCore {
  audit: IAuditConfig,
  auditSlug: string,
  checkIds: string[],
  auditResults:IAuditResult[],
  auditResultsMap: Map<string, IAuditResult>,
  questions: IQuestionConfig[],
  mscatPanels?: IPanelModulesConfig[], // If were auditing mscat panels collect them in this property
  tloftPanels?: IAssembledPanel[],
  linearSections?: IAssessmentPartition[],
}

export interface AuditResultMeta {
  // auditResultsModel?:AuditResultCore,
  isCustomResults?:boolean,
}

export const  processCheck = async (check:TAuditCheck, auditResultsMap:Map<string, IAuditResult>, questions: IQuestionConfig[], ctx:IContext, psychStatsData?: Map<number, any>) => {
  const flaggedQuestions = auditResultsMap.get(check.id).items; // to store
  if(check.checkConfig){
   await _processCheck(check, questions, ctx, psychStatsData, flaggedQuestions)
  }
  const result = auditResultsMap.get(check.id);
  if (result && ctx.frameworkCtrl) {
    const collected = ctx.frameworkCtrl.getCollectedParamCodes(check.id);
    if (collected?.length) {
      result.collectedParamCodes = collected;
    }
  }
}

const _processCheck = async(check:Partial<TAuditCheck>, questions: IQuestionConfig[], ctx: IContext, psychStatsData?: Map<number, any>, flaggedQuestions?: IQuestionConfig[]) =>{
    switch (check.checkType){
      case AuditCheckType.PROP_VAL: return processCheckPropVal( check, questions, flaggedQuestions, ctx);
      case AuditCheckType.PROPS_CONFIG: return processCheckPropsConfig( check, questions, flaggedQuestions, ctx);
      case AuditCheckType.PROPS_CONFIG_OR: return processCheckPropsConfig( check, questions, flaggedQuestions, ctx, true);
      case AuditCheckType.BY_BLOCK_TYPE: return processCheckByBlockType( check, questions, flaggedQuestions, ctx);
      case AuditCheckType.ITEM_SETTINGS: return processCheckByItemSettings( check, questions, flaggedQuestions, ctx);
      case AuditCheckType.PROPS_CONFIG_PSYCH_STATS: return processCheckPropsConfigPsychStats( check, questions, flaggedQuestions, ctx, false, psychStatsData);
      case AuditCheckType.PROPS_CONFIG_PSYCH_STATS_OR: return processCheckPropsConfigPsychStats( check, questions, flaggedQuestions, ctx, true, psychStatsData);
      case AuditCheckType.BY_CONTAINS_BLOCK_TYPE : return processCheckByContainsBlockType( check, questions, flaggedQuestions, ctx);
      case AuditCheckType.BY_FUNCTION : return processCheckByFunction(check, questions, flaggedQuestions, ctx);
      case AuditCheckType.COMPOSITE : return processCompositeCheck(check, questions, flaggedQuestions, ctx, psychStatsData);
      case AuditCheckType.COMPOSITE_OR : return processCompositeCheck(check, questions, flaggedQuestions, ctx, psychStatsData, true);
    }
}

export const processCompositeCheck = async( check: Partial<TAuditCheck>, questionsToSearch:IQuestionConfig[], flaggedQuestions:IQuestionConfig[], ctx:IContext, psychStatsData?: Map<number, any>, singleConditionRequired?: boolean) => {
  const checkConfig = <Partial<TAuditCheck>[]>check.checkConfig;
  const checkFlaggedQuestions:Set<any>[] = [];
  await Promise.all(checkConfig.map(async (check) =>{
    const _flaggedQuestions = [];
    await _processCheck(check, questionsToSearch, ctx, psychStatsData, _flaggedQuestions);
    checkFlaggedQuestions.push(new Set(_flaggedQuestions));
    return;
  }));
  if (singleConditionRequired) {
    const singleConditionFlaggedQuestions = new Set<any>();

    checkFlaggedQuestions.forEach(flaggedQSet => {
      // Don't add the set/array itself!
      // Iterate the set and add the specific items
      flaggedQSet.forEach(question => {
        singleConditionFlaggedQuestions.add(question);
      });
    });

    flaggedQuestions.push(...singleConditionFlaggedQuestions);
  } else {
    flaggedQuestions.push(...intersection(...checkFlaggedQuestions));
  }
}

export const processCheckByFunction = async (check: Partial<IAuditCheckByFunctions>, questionsToSearch: IQuestionConfig[], flaggedItems: (IQuestionConfig | number | IItemContentDiff | ILegacyItemContentDiff | boolean | IAssembledPanel | IPanelModulesConfig | {id:null} | {} | IAssessmentPartition)[], ctx:IContext) => {
  const {functionParams, functionId} = check.checkConfig ?? {};
  const checkFunction = ctx.customCheckFunctions[functionId];
  const flagged = await checkFunction(questionsToSearch, {...functionParams,  checkId: ctx.checkId, auditSlug: ctx.auditSlug}, check)
  flaggedItems?.push(...flagged);
}

export const processCheckByBlockType = async ( check:Partial<IAuditCheckByBlockType>, questionsToSearch:IQuestionConfig[], flaggedQuestions:IQuestionConfig[], ctx:IContext) => {
  const checkConfig = check.checkConfig;
  // flaggedQuestions.push(qConfig);
  const {targetBlockType, relativePropPath, transforms, filterMethod, filterValue} = checkConfig;
  for(const qConfig of questionsToSearch){
    const blockElements = getQuestionDeepElements(qConfig);
    let isAnyFailed = false;
    for (let el of blockElements){

      if (targetBlockType == el.elementType){
        if (relativePropPath){
          let propVal = _.get(el, relativePropPath);
          if (transforms){
            for (let transform of transforms){
              propVal = applyPropTransform(propVal, transform.type, transform.config);
            }
          }
          if (filterMethod){
            if (checkPropCondVal(propVal, filterMethod, filterValue)){
              isAnyFailed = true;
            }
          }
          if (propVal){
            if (checkConfig.setHas){
              for (let valToCheck of checkConfig.setHas){
                if (propVal?.has(valToCheck)){
                  isAnyFailed = true;
                }
              }
            }
            if (checkConfig.setMissing){
              for (let valToCheck of checkConfig.setMissing){
                if (!propVal?.has(valToCheck)){
                  isAnyFailed = true;
                }
              }
            }
            if (checkConfig.setHasAll){
              let isAllFailed = true;
              for (let valToCheck of checkConfig.setHasAll){
                  isAllFailed &&= propVal?.has(valToCheck);
              }
              isAnyFailed ||= isAllFailed
            }
          }
        }
      }

      // todo: sub-optimal exception, but not refactoring now
      if (checkConfig.dndKeys){
        for(let key of checkConfig.dndKeys){
          if(key){
            isAnyFailed ||= await checkElDndMissingDupe(el, key);
          }
        }
      }

    }
    if (isAnyFailed){
      flaggedQuestions.push(qConfig);
    }
  }
}

const checkElDndMissingDupe = async (el:IContentElement, check:DndCheckFlags) => {
  const keyIdSet = new Set();
  const checks = {
    hasDuplicateKeyIds: false,
    hasMissingKeyIds: false,
    hasMissingHomeTargetIds: false,
    hasDuplicateHomeTargetIds: false,
  }
  switch (el.elementType) {
    case ElementType.MOVEABLE_DND:
    case ElementType.GROUPING:

      const dndEl = <IContentElementMoveableDragDrop | IContentElementGroup>el;

      const {draggables, targets} = dndEl;

      draggables?.forEach(obj => checkForMissingOrDuplicateKeyIds(obj, keyIdSet, checks));
      targets?.forEach(obj => checkForMissingOrDuplicateKeyIds(obj, keyIdSet, checks));

      // if(checks.hasMissingKeyIds) auditResultsMap.get(CheckId.MISSING_KEY_ID).items.push(qConfig);
      // if(checks.hasDuplicateKeyIds) auditResultsMap.get(CheckId.ITEMS_WITH_DUPLICATE_KEY_ID).items.push(qConfig);

      if(el.elementType === ElementType.MOVEABLE_DND) {
        const homeTargetIds = new Set();
        draggables?.forEach(draggable => {
          if(!draggable.id){
            checks.hasMissingHomeTargetIds = true;
            // auditResultsMap.get(CheckId.MISSING_HOME_TARGET_ID).items.push(qConfig);
          }
          else if(homeTargetIds.has(draggable.id)){
            checks.hasDuplicateHomeTargetIds = true;
            // auditResultsMap.get(CheckId.ITEMS_WITH_DUPLICATE_HOME_TARGET_ID).items.push(qConfig);
          }
          else { 
            homeTargetIds.add(draggable.id) 
          }
        })
      }

      break;

    case ElementType.ORDER:
      const orderEl = <IContentElementOrder>el;
      orderEl.options?.forEach(order => checkForMissingOrDuplicateKeyIds(order, keyIdSet, checks));
      // if(checks.hasMissingKeyIds) auditResultsMap.get(CheckId.MISSING_KEY_ID).items.push(qConfig);
      // if(checks.hasDuplicateKeyIds) auditResultsMap.get(CheckId.ITEMS_WITH_DUPLICATE_KEY_ID).items.push(qConfig);
      break;

    case ElementType.INSERTION:
      const insertEl = <IContentElementInsertion>el;
      const insertTargets = insertEl.textBlocks?.filter(block => block.element.elementType === ElementType.BLANK || block.element.elementType === ElementType.BLANK_DEPRECIATED) ;  
      
      insertEl.draggables?.forEach(draggable => checkForMissingOrDuplicateKeyIds(draggable, keyIdSet, checks));
      insertTargets.forEach(target => checkForMissingOrDuplicateKeyIds(target, keyIdSet, checks))
      // if(checks.hasMissingKeyIds) auditResultsMap.get(CheckId.MISSING_KEY_ID).items.push(qConfig);
      // if(checks.hasDuplicateKeyIds) auditResultsMap.get(CheckId.ITEMS_WITH_DUPLICATE_KEY_ID).items.push(qConfig);                  
      break;
      
    default:
      break;
  }
  return checks[check];
}

const checkForMissingOrDuplicateKeyIds = (obj, keyIdSet: Set<any>, checks: {hasDuplicateKeyIds: boolean, hasMissingKeyIds:boolean} ) => {
  const key_id = obj.key_id?.toString()?.trim();
  if(!key_id){
    checks.hasMissingKeyIds = true;
  }
  else if(keyIdSet.has(key_id)){
    checks.hasDuplicateKeyIds = true;
  }
  else { 
    keyIdSet.add(key_id) 
  }
  // console.log(key_id, keyIdSet, keyIdSet.has(key_id), checks)
}

export const processCheckPropVal = async ( check:Partial<IAuditCheckByPropVal>, questionsToSearch:IQuestionConfig[], flaggedQuestions:IQuestionConfig[], ctx:IContext) => {
  const checkConfig = check.checkConfig;
  const {targetProp, filterMethod, filterValue, isScoringConfig, filterValueParam} = checkConfig;
  if (targetProp){
    for(const qConfig of questionsToSearch){
      let valContainer;
      if (isScoringConfig){
        valContainer = ctx.itemBankCtrl.getQuestionScoringInfo(qConfig.id)
      }
      else {
        const {meta} = qConfig;
        valContainer = meta;
      }
      const propVal = _.get(valContainer || {}, targetProp);
      const targetPropVal = _.get(valContainer || {}, filterValueParam);
      if (checkPropCondVal(propVal, filterMethod, filterValue ?? targetPropVal)){
        flaggedQuestions.push(qConfig);
      }
    }
  }
}

/**
 * Function used to check if certain prop configs are met (combination of question metas)
 * @param check 
 * @param questionsToSearch 
 * @param flaggedQuestions 
 * @param ctx 
 * @param singleConditionRequired // if only one of the configs is required for question to be flagged
 */
export const processCheckPropsConfig = async ( check:Partial<IAuditCheckByPropConfig>, questionsToSearch:IQuestionConfig[], flaggedQuestions:IQuestionConfig[], ctx:IContext, singleConditionRequired: boolean = false) => {
  const checkConfig = check.checkConfig;

  for (const qConfig of questionsToSearch) {
    const { meta } = qConfig;
    const valContainer = meta;
    let meetsAllConfigs = true;
    let numOfConfigsMet = 0;
    for (const config of checkConfig) {
      const { targetProp, filterMethod, filterValue} = config;
      if (targetProp) {
        const propVal = _.get(valContainer || {}, targetProp);
        if (!checkPropCondVal(propVal, filterMethod, filterValue)) {
          meetsAllConfigs = false;
          if(!singleConditionRequired){ break;}
        } else  {
          numOfConfigsMet++;
        }
      }
    }

    if (meetsAllConfigs || (singleConditionRequired && numOfConfigsMet !== 0)) {
      flaggedQuestions.push(qConfig);
    }
  }
}

/**
 * Function used to check if certain prop configs are met (combination of question metas)
 * @param check 
 * @param questionsToSearch 
 * @param flaggedQuestions 
 * @param ctx 
 * @param singleConditionRequired // if only one of the configs is required for question to be flagged
 */
export const processCheckPropsConfigPsychStats = async ( check:Partial<IAuditCheckByPropConfig>, questionsToSearch:IQuestionConfig[], flaggedQuestions:IQuestionConfig[], ctx:IContext, singleConditionRequired: boolean = false, psychStatsData?: Map<number, any>) => {
  const checkConfig = check.checkConfig;

  for (const qConfig of questionsToSearch) {
    const meta = psychStatsData?.get(qConfig.id) || [{}];
    const valContainer = meta;
    let meetsAllConfigs = true;
    let numOfConfigsMet = 0;
    for (const config of checkConfig) {
      const { targetProp, filterMethod, filterValue } = config;
      if (targetProp) {
        let propMatched = false;
        for (const container of valContainer) {
          const propVal = _.get({...container, ...qConfig.meta}, targetProp);
          if (checkPropCondVal(propVal, filterMethod, filterValue)) {
            propMatched = true;
            numOfConfigsMet++;
            if (singleConditionRequired) { break; }
          }
        }
        if (!propMatched) {
          meetsAllConfigs = false;
          if (!singleConditionRequired) { break; }
        }
      }
    }

    if (meetsAllConfigs || (singleConditionRequired && numOfConfigsMet !== 0)) {
      flaggedQuestions.push(qConfig);
    }
  }
}

function processCheckByItemSettings(check: Partial<IAuditCheckByPropVal>, questionsToSearch: IQuestionConfig[], flaggedQuestions: IQuestionConfig[], ctx: IContext) {
  const checkConfig = check.checkConfig;
  const {targetProp, filterMethod, filterValue, isScoringConfig} = checkConfig;
  if (targetProp){
    for(const qConfig of questionsToSearch){
      const propVal = _.get( qConfig, targetProp);
      if (checkPropCondVal(propVal, filterMethod, filterValue)){
        flaggedQuestions.push(qConfig);
      }
    }
  }
}

function processCheckByContainsBlockType(check: Partial<IAuditCheckByContainsBlockType>, questionsToSearch: IQuestionConfig[], flaggedQuestions: IQuestionConfig[], ctx: IContext) {
  const checkConfig = check.checkConfig;
  switch(checkConfig.targetElementGrouping) {
    case TargetElementGrouping.RESPONSE_TYPE_BLOCK_MARKED_AS_READING:
      filterToResponseBlocks(questionsToSearch).forEach((qconfig) => {
        if (qconfig.isReadingSelectionPage) {
          flaggedQuestions.push(qconfig);
        }
      })
  }
}

function filterToResponseBlocks(questionsToSearch: IQuestionConfig[]) {
    const questionsWithRespondables: IQuestionConfig[] = [];
    for (const qconfig of questionsToSearch) {
      const respondables = getQuestionRespondableDeep(qconfig);
      if (respondables.length) {
        questionsWithRespondables.push(qconfig);
      }
    }
    return questionsWithRespondables;
}

/**
 * Function that determines if an audit is included in the issue count
 * @param informativeOnlyCheck the value for informative of the subaudit (takes priority)
 * @param globalInformativeOnlyCheck the parent audits value of informative only (defaults to false)
 * @param ctx context for more complex checks to determine if it is informative only
 * @returns a boolean determining if the audit is informative only
 */
export const proccessInformativeOnly = (informativeOnlyCheck: InformativeCheckType, globalInformativeOnlyCheck: InformativeCheckType = false, ctx: IContext) : boolean =>{
  let activeInformativeCheck = informativeOnlyCheck;
  if(isNullOrEmptyOrUndefined(informativeOnlyCheck)){
      activeInformativeCheck = activeInformativeCheck || globalInformativeOnlyCheck
  }
  switch(activeInformativeCheck){
      case 'NOT_SAMPLE_TEST':
        return !ctx.frameworkCtrl.asmtFmrk.isSampleTest;
      case 'SAMPLE_TEST':
        return !!ctx.frameworkCtrl.asmtFmrk.isSampleTest;
      case 'IS_NOT_ALTERNATIVE':
        return !ctx.frameworkCtrl.asmtFmrk.isAlternativeLinearTest
      case 'IS_ALTERNATIVE':
        return !!ctx.frameworkCtrl.asmtFmrk.isAlternativeLinearTest
      default:
        return activeInformativeCheck;
  }
}

export const checkIfStringContainsSpecialKeyboardChars = (str:string) => {
      const SpecialCharacters = [
              {beginning: '<strike>', end: '</strike>'},
              {beginning: '<sup>', end: '</sup>'},
              {beginning: '<sub>', end: '</sub>'},
              {beginning: '<b>', end: '</b>'},
              {beginning: '<i>', end: '</i>'},
              {beginning: '<u>', end: '</u>'},
              {beginning: '\\*\\*', end: '\\*\\*'},
              {beginning: '\\*', end: '\\*'},
              {beginning: '<u>', end: '</u>'},
              {beginning: '<strike>', end: '</strike>'},
              {beginning: '', end: '&mdash;'},
              {beginning: '', end: '&ndash;'},
              {beginning: '', end: '&colon;'},
              {beginning: '', end: '&deg;'},
              {beginning: '', end: '&nbsp;'},

      ];
      for(let char of SpecialCharacters){
        if(char.beginning){
          let regex = new RegExp(`${char.beginning}.*?${char.end}`, "g");

        if (regex.test(str)) {
            return true
        }
        } else {
          if(typeof str === 'string' && str?.toLowerCase()?.includes(char.end)){
            return true;
          };
        }
      }
      return false
}

export const getCloneLabelCore = (label: string) => label.split('/')[0];

/**
 * Retrieves values found in ALL provided sets (Intersection operation).
 * @param {Set} first - The first set to compare against.
 * @param {...Set} rest - The remaining sets to compare with.
 * @returns {Set} A new Set containing only values present in all input sets.
 */
const intersection = <T>(...sets: Set<T>[]): Set<T> => {
  if (!sets || !sets?.length) return new Set<T>();
  const [first, ...rest] = sets;
  return new Set(
    [...first].filter(value => rest.every(otherSet => otherSet.has(value)))
  );
}

export const filterArray = (arrayFilter: ITargetPropArrayFilterConfig[] | undefined, arr: any[], onlyOneRequired: boolean = false) =>{
  if(arrayFilter && arrayFilter.length){
    arr = arr.filter(el =>{
      if(onlyOneRequired){
        return arrayFilter.some(config => {
          const { target, filterMethod, val, isValPath } = config;
          const targVal = _.get(el, target)
          let compareVal = val;
          if (isValPath) {
            compareVal = _.get(el, val)
          }
          return checkPropCondVal(targVal, filterMethod, compareVal);
        })
      }
      return arrayFilter.every(config =>{
        const {target, filterMethod, val, isValPath} = config;
        const targVal = _.get(el, target)
        let compareVal = val;
        if(isValPath){
          compareVal = _.get(el, val)
        }
        return checkPropCondVal(targVal, filterMethod, compareVal);
      })
    })
  }
  return arr
}
